/**
 * Unified alert / toast / confirm API. Replaces:
 *   - showCustomAlert / hideCustomAlert (custom-alerts.js)
 *   - showToast x2 (custom-alerts.js + ui-helpers.js)
 *   - showConfirm / showError / showNotice / showSuccess (confirm-notices.js)
 *   - confirmDialog (ui-helpers.js)
 *
 * All variants share one DOM root injected lazily so the module is independent
 * of any specific HTML in index.html.
 */

const ROOT_ID = '__app_alerts_root';

function ensureRoot() {
  let root = document.getElementById(ROOT_ID);
  if (root) return root;
  root = document.createElement('div');
  root.id = ROOT_ID;
  root.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:99999;';
  document.body.appendChild(root);
  injectStyles();
  return root;
}

let stylesInjected = false;
function injectStyles() {
  if (stylesInjected) return;
  stylesInjected = true;
  const style = document.createElement('style');
  style.textContent = `
    /* === Rich alert (centered icon + badge + warm-tone card) ============= */
    #${ROOT_ID} .app-rich-card {
      background: #fff; border-radius: 24px;
      padding: 32px 24px 24px; max-width: 340px; width: 100%;
      box-shadow: 0 24px 64px rgba(15, 23, 42, 0.18);
      direction: rtl; text-align: center; font-family: inherit;
      animation: app-rich-in 0.25s cubic-bezier(0.16, 1, 0.3, 1);
    }
    @keyframes app-rich-in {
      from { opacity: 0; transform: translateY(12px) scale(0.96); }
      to   { opacity: 1; transform: translateY(0) scale(1); }
    }
    #${ROOT_ID} .app-rich-icon {
      width: 72px; height: 72px; margin: 0 auto 20px;
      border-radius: 50%;
      background: linear-gradient(135deg, #d1fae5, #a7f3d0);
      display: flex; align-items: center; justify-content: center;
      box-shadow: 0 8px 24px rgba(16, 185, 129, 0.25);
    }
    #${ROOT_ID} .app-rich-icon svg { width: 36px; height: 36px; fill: #047857; }
    #${ROOT_ID} .app-rich-badge {
      display: inline-flex; align-items: center; gap: 6px;
      font-size: 12px; color: #047857; font-weight: 600;
      background: #ecfdf5; padding: 6px 12px; border-radius: 999px;
      margin-bottom: 16px;
    }
    #${ROOT_ID} .app-rich-message {
      margin: 0 0 24px; font-size: 14px; line-height: 1.6; color: #334155;
    }
    #${ROOT_ID} .app-rich-btn {
      width: 100%; padding: 14px; border: 0; border-radius: 14px;
      background: linear-gradient(135deg, #14b8a6 0%, #6366f1 100%);
      color: #fff; font-size: 15px; font-weight: 700;
      cursor: pointer;
      box-shadow: 0 6px 16px rgba(99, 102, 241, 0.3);
      transition: transform 0.1s, box-shadow 0.15s;
    }
    #${ROOT_ID} .app-rich-btn:hover { box-shadow: 0 8px 20px rgba(99, 102, 241, 0.4); }
    #${ROOT_ID} .app-rich-btn:active { transform: scale(0.97); }

    /* === Plain alert / confirm (existing) =============================== */
    #${ROOT_ID} .app-alert-backdrop {
      position: fixed; inset: 0; background: rgba(0,0,0,0.5);
      display: flex; align-items: center; justify-content: center;
      pointer-events: auto; padding: 20px;
    }
    #${ROOT_ID} .app-alert-card {
      background: #fff; border-radius: 14px; padding: 20px; max-width: 360px; width: 100%;
      box-shadow: 0 10px 30px rgba(0,0,0,0.2); direction: rtl; text-align: center;
      font-family: inherit;
    }
    #${ROOT_ID} .app-alert-title { font-weight: 700; font-size: 17px; margin: 0 0 8px; }
    #${ROOT_ID} .app-alert-message { font-size: 15px; color: #2c3e50; margin: 0 0 16px; line-height: 1.45; }
    #${ROOT_ID} .app-alert-actions { display: flex; gap: 8px; justify-content: center; }
    #${ROOT_ID} .app-alert-btn {
      flex: 1; min-width: 90px; padding: 10px 16px; border-radius: 10px; border: 0;
      font-size: 15px; font-weight: 600; cursor: pointer; transition: opacity .15s;
    }
    #${ROOT_ID} .app-alert-btn:hover { opacity: 0.9; }
    #${ROOT_ID} .app-alert-btn-primary { background: #2196f3; color: #fff; }
    #${ROOT_ID} .app-alert-btn-secondary { background: #e0e6ed; color: #2c3e50; }
    #${ROOT_ID} .app-alert-btn-danger { background: #e74c3c; color: #fff; }
    #${ROOT_ID} .app-toasts {
      position: fixed; bottom: max(20px, var(--bottom-safe)); left: 50%;
      transform: translateX(-50%); display: flex; flex-direction: column; gap: 8px;
      pointer-events: none; max-width: calc(100vw - 32px);
    }
    #${ROOT_ID} .app-toast {
      background: #2c3e50; color: #fff; padding: 12px 18px; border-radius: 24px;
      font-size: 14px; box-shadow: 0 4px 12px rgba(0,0,0,0.2); pointer-events: auto;
      animation: app-toast-in .25s ease-out;
    }
    #${ROOT_ID} .app-toast.success { background: #27ae60; }
    #${ROOT_ID} .app-toast.error { background: #e74c3c; }
    #${ROOT_ID} .app-toast.warn { background: #f39c12; }
    @keyframes app-toast-in {
      from { opacity: 0; transform: translateY(20px); }
      to { opacity: 1; transform: translateY(0); }
    }
  `;
  document.head.appendChild(style);
}

/**
 * @typedef {Object} RichAlertOptions
 * @property {string} message
 * @property {string} [button='אישור']
 * @property {string} [badge]       Small label above the message (e.g. "🔒 החשבון שלך מוגן").
 * @property {string} [iconSvg]     The `<path d="..."/>` SVG path data only —
 *                                  the surrounding `<svg viewBox="0 0 24 24">`
 *                                  is added by the renderer.
 */

/**
 * Centered modal with a circular gradient icon, optional badge, and a single
 * full-width gradient primary button. Used for moments that benefit from a
 * gentler, more reassuring visual treatment than the bare `showAlert` (e.g.
 * "another device signed in — your account is safe").
 *
 * Returns a Promise that resolves when the user dismisses the dialog.
 *
 * @param {RichAlertOptions} opts
 * @returns {Promise<void>}
 */
export function showRichAlert(opts) {
  return new Promise((resolve) => {
    const root = ensureRoot();
    const backdrop = document.createElement('div');
    backdrop.className = 'app-alert-backdrop';
    const iconHtml = opts.iconSvg
      ? `<div class="app-rich-icon" aria-hidden="true">
           <svg viewBox="0 0 24 24">${opts.iconSvg}</svg>
         </div>`
      : '';
    const badgeHtml = opts.badge
      ? `<div class="app-rich-badge"></div>`
      : '';
    backdrop.innerHTML = `
      <div class="app-rich-card" role="alertdialog" aria-modal="true">
        ${iconHtml}
        ${badgeHtml}
        <p class="app-rich-message"></p>
        <button class="app-rich-btn"></button>
      </div>
    `;
    if (opts.badge) {
      /** @type {HTMLElement} */ (backdrop.querySelector('.app-rich-badge')).textContent = opts.badge;
    }
    /** @type {HTMLElement} */ (backdrop.querySelector('.app-rich-message')).textContent = opts.message;
    const btn = /** @type {HTMLButtonElement} */ (backdrop.querySelector('.app-rich-btn'));
    btn.textContent = opts.button ?? 'אישור';
    btn.addEventListener('click', () => {
      backdrop.remove();
      resolve();
    });
    root.appendChild(backdrop);
    btn.focus();
  });
}

/**
 * @typedef {Object} AlertOptions
 * @property {string} [title]
 * @property {string} message
 * @property {string} [button='אישור']
 */

/**
 * Show a single-button alert. Returns a Promise that resolves when dismissed.
 * @param {AlertOptions} opts
 * @returns {Promise<void>}
 */
export function showAlert(opts) {
  return new Promise((resolve) => {
    const root = ensureRoot();
    const backdrop = document.createElement('div');
    backdrop.className = 'app-alert-backdrop';
    backdrop.innerHTML = `
      <div class="app-alert-card" role="alertdialog" aria-modal="true">
        ${opts.title ? `<h3 class="app-alert-title"></h3>` : ''}
        <p class="app-alert-message"></p>
        <div class="app-alert-actions">
          <button class="app-alert-btn app-alert-btn-primary"></button>
        </div>
      </div>
    `;
    const titleEl = backdrop.querySelector('.app-alert-title');
    if (titleEl && opts.title) titleEl.textContent = opts.title;
    const msgEl = /** @type {HTMLElement} */ (backdrop.querySelector('.app-alert-message'));
    msgEl.textContent = opts.message;
    const btn = /** @type {HTMLButtonElement} */ (backdrop.querySelector('.app-alert-btn'));
    btn.textContent = opts.button ?? 'אישור';
    btn.addEventListener('click', () => {
      backdrop.remove();
      resolve();
    });
    root.appendChild(backdrop);
    btn.focus();
  });
}

/**
 * @typedef {Object} ConfirmOptions
 * @property {string} [title]
 * @property {string} message
 * @property {string} [confirm='אישור']
 * @property {string} [cancel='ביטול']
 * @property {boolean} [danger=false]
 */

/**
 * Show a confirm dialog. Resolves to true (confirmed) or false (cancelled).
 * @param {ConfirmOptions} opts
 * @returns {Promise<boolean>}
 */
export function showConfirm(opts) {
  return new Promise((resolve) => {
    const root = ensureRoot();
    const backdrop = document.createElement('div');
    backdrop.className = 'app-alert-backdrop';
    backdrop.innerHTML = `
      <div class="app-alert-card" role="alertdialog" aria-modal="true">
        ${opts.title ? `<h3 class="app-alert-title"></h3>` : ''}
        <p class="app-alert-message"></p>
        <div class="app-alert-actions">
          <button class="app-alert-btn app-alert-btn-secondary" data-act="cancel"></button>
          <button class="app-alert-btn ${opts.danger ? 'app-alert-btn-danger' : 'app-alert-btn-primary'}" data-act="confirm"></button>
        </div>
      </div>
    `;
    const titleEl = backdrop.querySelector('.app-alert-title');
    if (titleEl && opts.title) titleEl.textContent = opts.title;
    /** @type {HTMLElement} */ (backdrop.querySelector('.app-alert-message')).textContent = opts.message;
    const confirmBtn = /** @type {HTMLButtonElement} */ (backdrop.querySelector('[data-act="confirm"]'));
    const cancelBtn  = /** @type {HTMLButtonElement} */ (backdrop.querySelector('[data-act="cancel"]'));
    confirmBtn.textContent = opts.confirm ?? 'אישור';
    cancelBtn.textContent  = opts.cancel  ?? 'ביטול';
    const done = (/** @type {boolean} */ value) => { backdrop.remove(); resolve(value); };
    confirmBtn.addEventListener('click', () => done(true));
    cancelBtn.addEventListener('click', () => done(false));
    root.appendChild(backdrop);
    confirmBtn.focus();
  });
}

/**
 * @typedef {'info' | 'success' | 'warn' | 'error'} ToastKind
 */

/**
 * @typedef {Object} ToastOptions
 * @property {ToastKind} [kind='info']
 * @property {number} [duration=3000]
 */

/**
 * Show a transient toast that auto-dismisses.
 * @param {string} message
 * @param {ToastOptions} [options]
 */
export function showToast(message, options = {}) {
  const { kind = 'info', duration = 3000 } = options;
  const root = ensureRoot();
  let container = root.querySelector('.app-toasts');
  if (!container) {
    container = document.createElement('div');
    container.className = 'app-toasts';
    root.appendChild(container);
  }
  const toast = document.createElement('div');
  toast.className = `app-toast ${kind === 'info' ? '' : kind}`.trim();
  toast.textContent = message;
  container.appendChild(toast);
  setTimeout(() => {
    toast.style.opacity = '0';
    toast.style.transition = 'opacity .25s';
    setTimeout(() => toast.remove(), 260);
  }, duration);
}
